import { ScanResult, ScanSummary, createScanSummary } from './types';
import { supabase } from '@/integrations/supabase/client';

export async function runDBStatsIntegrityScan(): Promise<ScanSummary> {
  console.group('🗄️ DB Stats Integrity Scan');
  
  const results: ScanResult[] = [];

  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      results.push({
        name: 'Authentication Status',
        status: 'info',
        message: 'No authenticated user - cannot check database stats',
        details: 'Database stats scan requires authentication'
      });

      const summary = createScanSummary('DB Stats Integrity Scan', results);
      console.groupEnd();
      return summary;
    }

    // Fetch user stats
    const { data: stats, error: statsError } = await supabase
      .from('game_stats')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (statsError || !stats) {
      results.push({
        name: 'Stats Fetch',
        status: 'fail',
        message: 'Could not fetch user stats from database',
        details: statsError?.message || 'No stats found'
      });

      const summary = createScanSummary('DB Stats Integrity Scan', results);
      console.groupEnd();
      return summary;
    }

    results.push({
      name: 'Stats Fetch',
      status: 'pass',
      message: 'Successfully fetched user stats',
      details: `User: ${user.email}`
    });

    // Check 1: Streak logic (current <= max)
    const currentStreak = stats.current_streak || 0;
    const maxStreak = stats.max_streak || 0;

    results.push({
      name: 'Streak Logic Validation',
      status: currentStreak <= maxStreak ? 'pass' : 'fail',
      message: currentStreak <= maxStreak
        ? `Current streak (${currentStreak}) ≤ max streak (${maxStreak})`
        : `INVALID: Current streak (${currentStreak}) > max streak (${maxStreak})`,
      details: 'Current streak should never exceed max streak'
    });

    // Check 2: Win rate calculation
    const totalPlayed = stats.total_played || 0;
    const totalWon = stats.total_won || 0;
    const expectedWinRate = totalPlayed > 0 ? (totalWon / totalPlayed) * 100 : 0;

    results.push({
      name: 'Win Count Validation',
      status: totalWon <= totalPlayed ? 'pass' : 'fail',
      message: totalWon <= totalPlayed
        ? `Total won (${totalWon}) ≤ total played (${totalPlayed})`
        : `INVALID: Total won (${totalWon}) > total played (${totalPlayed})`,
      details: `Win rate: ${expectedWinRate.toFixed(1)}%`
    });

    // Check 3: Guess distribution sum matches total won
    const guessDistribution = stats.guess_distribution as Record<string, number> || {};
    const distributionSum = Object.values(guessDistribution).reduce((sum, count) => sum + count, 0);

    results.push({
      name: 'Guess Distribution Sum',
      status: distributionSum === totalWon ? 'pass' : 'warn',
      message: distributionSum === totalWon
        ? `Distribution sum (${distributionSum}) matches total won (${totalWon})`
        : `MISMATCH: Distribution sum (${distributionSum}) ≠ total won (${totalWon})`,
      details: `Distribution: ${JSON.stringify(guessDistribution)}`
    });

    // Check 4: Non-negative values
    const negativeChecks = [
      { name: 'total_played', value: totalPlayed },
      { name: 'total_won', value: totalWon },
      { name: 'current_streak', value: currentStreak },
      { name: 'max_streak', value: maxStreak }
    ];

    const negativeFields = negativeChecks.filter(check => check.value < 0);

    results.push({
      name: 'Non-Negative Values',
      status: negativeFields.length === 0 ? 'pass' : 'fail',
      message: negativeFields.length === 0
        ? 'All stat values are non-negative'
        : `Found ${negativeFields.length} negative value(s)`,
      details: negativeFields.length > 0
        ? negativeFields.map(f => `${f.name}=${f.value}`).join(', ')
        : 'All values valid'
    });

    // Check 5: Check game_progress count vs total_played (CRITICAL)
    const { data: progressEntries, error: progressError } = await supabase
      .from('game_progress')
      .select('*')
      .eq('user_id', user.id)
      .eq('completed', true);

    if (!progressError && progressEntries !== null) {
      const progressCount = progressEntries.length;
      const actualWonCount = progressEntries.filter(p => p.won).length;

      // This is the critical check - if game_stats doesn't match actual progress, stats are corrupt
      const countMismatch = progressCount !== totalPlayed || actualWonCount !== totalWon;

      results.push({
        name: 'Progress Count vs Total Played',
        status: countMismatch ? 'fail' : 'pass',
        message: countMismatch
          ? `❌ STATS MISMATCH: game_progress shows ${progressCount} played / ${actualWonCount} won, but game_stats shows ${totalPlayed} played / ${totalWon} won`
          : `✅ Progress entries (${progressCount}) match stats (${totalPlayed})`,
        details: countMismatch 
          ? '🔧 Run recalculate_user_stats() to fix. Add "Recalculate Stats" button in Security tab.'
          : 'Database integrity verified'
      });
    }

  } catch (error) {
    results.push({
      name: 'DB Stats Scan Error',
      status: 'fail',
      message: `Failed to run database stats scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('DB Stats Integrity Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
